import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Form, Nav } from 'react-bootstrap';
import { FaPlus } from 'react-icons/fa';
import { upperFirst } from 'lodash';
import { t } from 'i18next';
import { useDispatch, useSelector } from 'react-redux';
import TemplateNameModel from './template-name-model';
import TemplateItemsModel from './template-items.model';
import Table from '../../../components/Table';
import DeleteConfirmation from '../../../components/DeleteConfirmation';
import { deleteTemplate, getTemplates } from '../../../redux/template';
import { getComplains, getFrequencies, getDurations, getSeverities, getDiagnosis, getMedicine, getDose, getTiming, getTestRequired, getAdvice, getDietExercises, getGeneral, getCvs, getRs, getCns, getPa, getEnt } from '../../../redux/visit';
import { resetTemplates, setTemplatesDetails } from '../../../redux/template/slice';
import { useTemplateColumn, useTemplateType } from './hook';
import useDebounce from '../../../utils/useDebounceHook';

const Templates = () => {
	const [title, setTitle] = useState('');
	const [model, setModel] = useState(false);
	const [itemsModel, setItemsModel] = useState(false);
	const [deleteModel, setDeleteModel] = useState(false);
	const [deleteId, setDeleteId] = useState(null);
	const [type, setType] = useState('complaint');
	const [searchText, setSearchText] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const [perPage, setPerPage] = useState(10);
	const [columnSort, setColumnSort] = useState({});
	const dispatch = useDispatch();
	const { loading, list, total } = useSelector(state => state.templates);

	const search = useDebounce(searchText, 500);
	const templateTypes = useTemplateType(t);

	useEffect(() => {
		const lable = templateTypes.find((item) => item.value === type)?.label;
		setTitle(lable);
	}, [type]);

	useEffect(() => {
		fetchTemplateByType();
		return () => {
			dispatch(resetTemplates());
		};
	}, [currentPage, perPage, search, columnSort, type, dispatch]);

	const changeType = useCallback((type) => {
		setSearchText('');
		setPerPage(10);
		setCurrentPage(1);
		setColumnSort({});
		setType(type);
	}, []);

	const fetchTemplateByType = useCallback(() => {
		dispatch(getTemplates({ type: type.toLowerCase(), page: currentPage, per_page: perPage, search, ...columnSort }));
	}, [currentPage, perPage, search, type, columnSort, dispatch]);

	const handlePageChange = useCallback(page => {
		setCurrentPage(page);
	}, []);

	const handlePerRowsChange = useCallback(async(newPerPage) => {
		setPerPage(newPerPage);
		setCurrentPage(1);
	}, []);

	const handleSort = useCallback(
		(column, sortDirection) => {
			setColumnSort({ column_name: column.sortField, order: sortDirection });
		}, []);

	const setTemplateNameDetails = (row) => {
		dispatch(setTemplatesDetails(row));
		setModel(true);
	};

	const setTemplateItemsDetails = (row) => {
		dispatch(setTemplatesDetails(row));
		setItemsModel(true);
	};

	const handleAddTemplate = () => {
		setItemsModel(true);
	};

	const handleDelete = (row) => {
		setDeleteId(row.id);
		setDeleteModel(true);
	};

	const handleDeleteConfirm = async() => {
		await dispatch(deleteTemplate(deleteId)).unwrap().then(() => {
			fetchTemplateByType();
			setDeleteModel(false);
			setDeleteId(null);
		}).catch((err) => {
			return err;
		});
	};

	const closeModel = () => {
		dispatch(setTemplatesDetails(null));
		setModel(false);
		setItemsModel(false);
	};

	const fetchDynamicData = () => {
		dispatch(getComplains({ per_page: 100, status: 'active' }));
		dispatch(getDiagnosis({ per_page: 100, status: 'active' }));
		dispatch(getMedicine({ per_page: 100, status: 'active' }));
	};

	useEffect(() => {
		fetchDynamicData();
		dispatch(getFrequencies({ per_page: 100 }));
		dispatch(getDurations({ per_page: 100 }));
		dispatch(getSeverities({ per_page: 100 }));
		dispatch(getDose({ per_page: 100 }));
		dispatch(getTiming({ per_page: 100 }));
		dispatch(getTestRequired({ per_page: 100, status: 'active' }));
		dispatch(getAdvice({ per_page: 100, status: 'active' }));
		dispatch(getDietExercises({ per_page: 100, status: 'active' }));
		dispatch(getGeneral({ per_page: 100, status: 'active' }));
		dispatch(getCvs({ per_page: 100, status: 'active' }));
		dispatch(getRs({ per_page: 100, status: 'active' }));
		dispatch(getCns({ per_page: 100, status: 'active' }));
		dispatch(getPa({ per_page: 100, status: 'active' }));
		dispatch(getEnt({ per_page: 100, status: 'active' }));
	}, []);

	useEffect(() => {
		fetchDynamicData();
	}, [list]);

	const columns = useTemplateColumn({ t, currentPage, perPage, handleNameEdit: setTemplateNameDetails, handleItemEdit: setTemplateItemsDetails, handleDelete: handleDelete, title, type });

	return (
		<Card>
			<Card.Header className='bg-white fw-bold d-flex justify-content-between align-items-center'>
				<span>{title}</span>
				<div className='d-flex align-items-center'>
					<Form.Control
						type="search"
						placeholder={t('GENERAL.SEARCHTEXT')}
						size="sm"
						className='form-control d-inline-block w-auto me-2'
						value={searchText}
						onChange={(e) => setSearchText(e.target.value)}
					/>
					<Button variant="success" onClick={handleAddTemplate} size="sm" className='d-inline-flex align-items-center'>
						<FaPlus className='me-1' /> {t('ALLOPATHY.ADD_BUTTON', { type: upperFirst(title) })}
					</Button>
				</div>
			</Card.Header>
			<Card.Body>
				<div style={{ overflowX: 'auto', overflowY: 'hidden', whiteSpace: 'nowrap' }} >
					<Nav
						variant="tabs"
						activeKey={type}
						onSelect={(k) => changeType(k)}
						className="flex-nowrap"
					>
						{templateTypes.map((item) => (
							<Nav.Item key={item.value}>
								<Nav.Link eventKey={item.value}>{item.label}</Nav.Link>
							</Nav.Item>
						))}
					</Nav>
				</div>
				<Table
					columns={columns}
					data={list}
					loading={loading}
					handlePageChange={handlePageChange}
					perPage={perPage}
					handlePerRowsChange={handlePerRowsChange}
					total={total}
					handleSort={handleSort}
				/>
			</Card.Body>
			<TemplateNameModel
				show={model}
				close={closeModel}
				fetch={fetchTemplateByType}
				type={type}
				title={title}
			/>
			<TemplateItemsModel
				show={itemsModel}
				close={closeModel}
				type={type}
				title={title}
				handleItemSubmit={() => {}}
				search={search}
			/>
			<DeleteConfirmation
				showModal={deleteModel}
				confirmModal={handleDeleteConfirm}
				hideModal={() => {
					setDeleteModel(false);
					setDeleteId(null);
				}}
				id={deleteId}
				deleteRecord={() => {}}
				message={'Are you sure you want to delete this template?'}
				title={t('GENERAL.DELETETITLE')}
				buttonText={t('GENERAL.DELETE')}
				loading={false}
			/>
		</Card>
	);
};

export default Templates;